/**
 * InvoiceEditor Component
 * Allows manual editing of all invoice fields
 */

import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import type { Invoice, LineItem } from '@shared/schemas/invoice.schema';
import { LogoUploader } from './LogoUploader';
import { SignStampUploader } from './SignStampUploader';
import { CurrencyDropdown } from '@/components/common/CurrencyDropdown';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { useHistoryStore } from '@/stores/historyStore';
import { isDefaultLogo } from '@/utils/logoStorage';
import { isDefaultSignStamp } from '@/utils/signStampStorage';
import { logger } from '@/utils/logger';
import {
  PaperPreset,
  createPaperSize,
} from '@/types/paper';

interface InvoiceEditorProps {
  invoice: Invoice;
  className?: string;
  compact?: boolean;
}

export function InvoiceEditor({ invoice, className = '', compact = false }: InvoiceEditorProps) {
  const { t } = useTranslation();
  const { updateInvoiceField, updateLineItem, addLineItem, removeLineItem, currentInvoice } = useInvoiceStore();
  const { updateInHistory } = useHistoryStore();

  // Use invoice's paperSize or default
  const invoicePaperSize = invoice.paperSize || { preset: 'A4' as const, width: 210, height: 297, orientation: 'portrait' as const };

  // Track previous invoice to detect changes
  const previousInvoiceRef = useRef<string | undefined>(undefined);

  // Auto-save changes to history
  useEffect(() => {
    // Only save if the invoice has an ID (was loaded from history or generated)
    if (currentInvoice && currentInvoice.id) {
      const currentJson = JSON.stringify(currentInvoice);

      // Only update if something actually changed
      if (previousInvoiceRef.current !== currentJson) {
        logger.info('[InvoiceEditor] Invoice changed, updating in history');
        updateInHistory(currentInvoice);
        previousInvoiceRef.current = currentJson;
      }
    }
  }, [currentInvoice, updateInHistory]);

  const handleLogoChange = (logoData: string | null) => {
    // Update the invoice's logo without affecting the default logo
    // This allows per-invoice customization
    logger.info('[InvoiceEditor] Logo changed:', logoData ? 'New logo uploaded' : 'Logo removed');
    logger.info('[InvoiceEditor] Is using default logo:', isDefaultLogo(invoice.fromLogo || ''));
    updateInvoiceField('fromLogo', logoData || undefined);
  };

  const handleSignStampChange = (signStampData: string | null) => {
    // Update the invoice's sign/stamp without affecting the default sign/stamp
    // This allows per-invoice customization
    logger.info('[InvoiceEditor] Sign/stamp changed:', signStampData ? 'New sign/stamp uploaded' : 'Sign/stamp removed');
    logger.info('[InvoiceEditor] Is using default sign/stamp:', isDefaultSignStamp(invoice.fromSignStamp || ''));
    updateInvoiceField('fromSignStamp', signStampData || undefined);
  };

  const isUsingDefaultLogo = invoice.fromLogo ? isDefaultLogo(invoice.fromLogo) : false;
  const isUsingDefaultSignStamp = invoice.fromSignStamp ? isDefaultSignStamp(invoice.fromSignStamp) : false;

  const localLineItems = invoice.lineItems;

  const handleFieldChange = (field: keyof Invoice, value: any) => {
    updateInvoiceField(field, value);
  };

  const handleLineItemChange = (id: string, field: keyof LineItem, value: string | number) => {
    const numValue = field === 'description' ? value : Number(value);
    updateLineItem(id, { [field]: numValue });
  };

  const handleAddLineItem = () => {
    addLineItem();
  };

  const handleRemoveLineItem = (id: string) => {
    if (localLineItems.length > 1) {
      removeLineItem(id);
    }
  };

  const sectionClass = compact ? 'mb-3' : 'mb-6';
  const headerClass = compact ? 'text-sm font-semibold mb-2' : 'text-lg font-semibold mb-3';
  const gridGap = compact ? 'gap-2' : 'gap-4';
  const inputPadding = compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2';

  return (
    <div className={`invoice-editor ${className}`}>
      <div className={compact ? 'space-y-3' : 'space-y-6'}>
        {/* Logo and Sign/Stamp Upload */}
        <div className={`editor-section ${sectionClass}`}>
          <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
            {t('invoice.editor.optionalImages')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Logo */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('invoice.editor.companyLogo')}
              </p>
              {!compact && isUsingDefaultLogo && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('invoice.editor.usingDefaultLogo')}
                </p>
              )}
              <LogoUploader
                currentLogo={invoice.fromLogo}
                onLogoChange={handleLogoChange}
              />
            </div>

            {/* Sign/Stamp Upload */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('invoice.editor.signStamp')}
              </p>
              {!compact && isUsingDefaultSignStamp && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Using default sign/stamp from InvoiceForm
                </p>
              )}
              <SignStampUploader
                currentSignStamp={invoice.fromSignStamp}
                onSignStampChange={handleSignStampChange}
              />
            </div>
          </div>
        </div>

        {/* Document Type and Paper Size */}
        <div className={`editor-section ${sectionClass}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Document Type Selection */}
            <div>
              <label htmlFor="editor-document-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Document Type
              </label>
              <select
                id="editor-document-type"
                value={invoice.documentType || 'invoice'}
                onChange={(e) => handleFieldChange('documentType', e.target.value as 'invoice' | 'receipt')}
                className={`${inputPadding} w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500`}
              >
                <option value="invoice">Invoice</option>
                <option value="receipt">Receipt</option>
              </select>
            </div>

            {/* Paper Size Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('invoice.editor.paperSize', 'Paper Size')}
              </label>
              <div className="flex gap-2">
                <select
                  value={invoicePaperSize.preset}
                  onChange={(e) => handleFieldChange('paperSize', createPaperSize(e.target.value as PaperPreset, invoicePaperSize.orientation))}
                  className={`${inputPadding} flex-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  {(['A4', 'A5', 'Letter', 'Legal'] as PaperPreset[]).map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => handleFieldChange('paperSize', createPaperSize(
                    invoicePaperSize.preset,
                    invoicePaperSize.orientation === 'portrait' ? 'landscape' : 'portrait'
                  ))}
                  className={`${inputPadding} rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2`}
                  aria-label={`Current orientation: ${invoicePaperSize.orientation}, click to change`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {invoicePaperSize.orientation === 'portrait' ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 2v4m0 0l-4 4m0 0l4-4m0 0v4m0 0H4m16 0h-8M4 18h16" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    )}
                  </svg>
                  <span className="capitalize">{invoicePaperSize.orientation}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className={`editor-section ${sectionClass}`}>
          <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
            {t('invoice.editor.invoiceDetails')}
          </h3>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${gridGap}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.invoiceNumber')}
              </label>
              <input
                type="text"
                value={invoice.invoiceNumber || ''}
                onChange={(e) => handleFieldChange('invoiceNumber', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.currency')}
              </label>
              <CurrencyDropdown
                value={invoice.currency}
                onChange={(currency) => handleFieldChange('currency', currency)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.invoiceDate')}
              </label>
              <input
                type="date"
                value={new Date(invoice.invoiceDate).toISOString().split('T')[0]}
                onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.dueDate')}
              </label>
              <input
                type="date"
                value={invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange('dueDate', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
          </div>
        </div>

        {/* From Company */}
        <div className={`editor-section ${sectionClass}`}>
          <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
            {t('invoice.editor.fromCompany')}
          </h3>
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.companyName')} *
              </label>
              <input
                type="text"
                value={invoice.fromCompany}
                onChange={(e) => handleFieldChange('fromCompany', e.target.value)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.email')}
              </label>
              <input
                type="email"
                value={invoice.fromEmail || ''}
                onChange={(e) => handleFieldChange('fromEmail', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.phone')}
              </label>
              <input
                type="text"
                value={invoice.fromPhone || ''}
                onChange={(e) => handleFieldChange('fromPhone', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.address')}
              </label>
              <textarea
                value={invoice.fromAddress || ''}
                onChange={(e) => handleFieldChange('fromAddress', e.target.value || undefined)}
                rows={compact ? 2 : 3}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
          </div>
        </div>

        {/* To Company */}
        <div className={`editor-section ${sectionClass}`}>
          <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
            {t('invoice.editor.billTo')}
          </h3>
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.companyName')} *
              </label>
              <input
                type="text"
                value={invoice.toCompany}
                onChange={(e) => handleFieldChange('toCompany', e.target.value)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.email')}
              </label>
              <input
                type="email"
                value={invoice.toEmail || ''}
                onChange={(e) => handleFieldChange('toEmail', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.phone')}
              </label>
              <input
                type="text"
                value={invoice.toPhone || ''}
                onChange={(e) => handleFieldChange('toPhone', e.target.value || undefined)}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.address')}
              </label>
              <textarea
                value={invoice.toAddress || ''}
                onChange={(e) => handleFieldChange('toAddress', e.target.value || undefined)}
                rows={compact ? 2 : 3}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className={`editor-section ${sectionClass}`}>
          <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-3'}`}>
            <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
              {t('invoice.editor.lineItems')}
            </h3>
            <button
              type="button"
              onClick={handleAddLineItem}
              className={`${compact ? 'px-2 py-1 text-xs' : 'px-4 py-2 text-sm'} bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium`}
            >
              + {t('invoice.editor.addItem')}
            </button>
          </div>
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            {invoice.lineItems.map((item) => (
              <div key={item.id} className={`line-item-card border border-gray-200 dark:border-gray-700 rounded-lg ${compact ? 'p-2' : 'p-4'}`}>
                <div className={`grid grid-cols-1 md:grid-cols-12 ${gridGap}`}>
                  <div className="md:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('invoice.editor.description')}
                    </label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                      className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('invoice.editor.quantity')}
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                      className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('invoice.editor.unitPrice')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => handleLineItemChange(item.id, 'unitPrice', e.target.value)}
                      className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <div className="w-full">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium text-gray-900 dark:text-gray-100 ${compact ? 'text-xs' : 'text-sm'}`}>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: invoice.currency,
                          }).format(item.quantity * item.unitPrice)}
                        </span>
                        {invoice.lineItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(item.id)}
                            className="ml-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            aria-label="Remove line item"
                          >
                            <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tax, Discount, Shipping, and Notes */}
        <div className={`editor-section ${sectionClass}`}>
          <h3 className={`${headerClass} text-gray-900 dark:text-gray-100`}>
            {t('invoice.editor.additionalDetails')}
          </h3>
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            <div className={`grid grid-cols-1 md:grid-cols-3 ${gridGap}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('invoice.editor.discountRate')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoice.discountRate || 0}
                  onChange={(e) => handleFieldChange('discountRate', Number(e.target.value))}
                  className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('invoice.editor.shipping')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoice.shipping || 0}
                  onChange={(e) => handleFieldChange('shipping', Number(e.target.value))}
                  className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('invoice.editor.taxRate')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoice.taxRate}
                  onChange={(e) => handleFieldChange('taxRate', Number(e.target.value))}
                  className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('invoice.editor.notes')}
              </label>
              <textarea
                value={invoice.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value || undefined)}
                rows={compact ? 2 : 4}
                placeholder={t('invoice.editor.notesPlaceholder')}
                className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
              />
            </div>
            <div className={`grid grid-cols-1 md:grid-cols-2 ${gridGap}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('invoice.editor.signStampName')}
                </label>
                <input
                  type="text"
                  value={invoice.fromSignStampName || ''}
                  onChange={(e) => handleFieldChange('fromSignStampName', e.target.value || undefined)}
                  placeholder={t('invoice.editor.signStampNamePlaceholder')}
                  className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('invoice.editor.signStampRole')}
                </label>
                <input
                  type="text"
                  value={invoice.fromSignStampRole || ''}
                  onChange={(e) => handleFieldChange('fromSignStampRole', e.target.value || undefined)}
                  placeholder={t('invoice.editor.signStampRolePlaceholder')}
                  className={`w-full ${inputPadding} border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
